/**
 * MODULE: platform/browser/frontend/data/browserMissions.ts
 * PURPOSE: Preset remote-browser missions (CRM, social, Salesforce) with start URLs and prompts.
 */

/** Start Browser toolbar default — loads reliably in headless Steel (Google often blocks embeds). */
export const BROWSER_QUICK_START_URL = 'https://www.example.com';

export type BrowserMissionId =
  | 'brazn-crm'
  | 'linkedin'
  | 'salesforce'
  | 'twitter'
  | 'google'
  | 'custom';

export type BrowserMission = {
  id: BrowserMissionId;
  label: string;
  description: string;
  icon: 'crm' | 'linkedin' | 'salesforce' | 'social' | 'search';
  /** Resolve start URL (may use window.location.origin for in-app CRM). */
  resolveStartUrl: () => string;
  prompts: string[];
};

function origin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:5173';
}

export const BROWSER_MISSIONS: BrowserMission[] = [
  {
    id: 'brazn-crm',
    label: 'Brazn CRM',
    description: 'Work inside your CRM — update deals, contacts, and tasks in Brazn.',
    icon: 'crm',
    resolveStartUrl: () => `${origin()}/crm`,
    prompts: [
      'Open the Deals hub and list deals closing this month',
      'Find the contact for Acme Corp and summarize their activity',
      'Create a follow-up task for the hottest open deal',
    ],
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    description: 'Research people and companies, prep outreach, and gather intel.',
    icon: 'linkedin',
    resolveStartUrl: () => 'https://www.linkedin.com',
    prompts: [
      'Search for the company and summarize their recent posts',
      'Find the CEO profile and list their latest activity',
      'Draft a personalized connection note based on their profile',
    ],
  },
  {
    id: 'salesforce',
    label: 'Salesforce',
    description: 'Update CRM records, log activities, and sync field data.',
    icon: 'salesforce',
    resolveStartUrl: () => 'https://login.salesforce.com',
    prompts: [
      'Find the account and update the primary contact email',
      'Log a call activity on the open opportunity',
      'Export the account overview fields to confirm changes',
    ],
  },
  {
    id: 'twitter',
    label: 'Social (X)',
    description: 'Monitor brand mentions and prospect social activity.',
    icon: 'social',
    resolveStartUrl: () => 'https://x.com',
    prompts: [
      'Search for recent mentions of the company name',
      'Summarize the last 5 posts from their official account',
    ],
  },
  {
    id: 'google',
    label: 'Web search',
    description: 'General research — news, competitors, and public data.',
    icon: 'search',
    resolveStartUrl: () => 'https://www.google.com',
    prompts: [
      'Search for recent news about this company and summarize',
      'Find the company website and extract the leadership team page',
    ],
  },
];

export function getBrowserMission(id: BrowserMissionId): BrowserMission | undefined {
  return BROWSER_MISSIONS.find((m) => m.id === id);
}

export function resolveMissionStartUrl(
  missionId: BrowserMissionId,
  customStartUrl?: string,
): string {
  if (missionId === 'custom' && customStartUrl?.trim()) {
    return customStartUrl.trim();
  }
  const mission = getBrowserMission(missionId);
  return mission?.resolveStartUrl() ?? BROWSER_QUICK_START_URL;
}

/** Suggested prompts that match the active start URL (avoids CRM chips on Google sessions). */
export function promptsForStartUrl(startUrl: string): string[] {
  const u = startUrl.toLowerCase();
  if (u.includes('linkedin.com')) {
    return getBrowserMission('linkedin')?.prompts ?? [];
  }
  if (u.includes('salesforce.com')) {
    return getBrowserMission('salesforce')?.prompts ?? [];
  }
  if (u.includes('x.com') || u.includes('twitter.com')) {
    return getBrowserMission('twitter')?.prompts ?? [];
  }
  if (u.includes('/crm')) {
    return getBrowserMission('brazn-crm')?.prompts ?? [];
  }
  return getBrowserMission('google')?.prompts ?? [];
}
